"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zUpdateTenantSettingsRequest = void 0;
const zod_1 = require("zod");
const money_js_1 = require("./money.js");
// Matches the DB's rate() column (numeric(9,5)) — e.g. "0.08250" for
// 8.25%. Kept a plain string, not a branded type like Money/Qty, matching
// how rates already appear elsewhere (e.g. InvoiceSummary.partsTaxRate).
const zRate = zod_1.z.string().regex(/^\d{1,4}\.\d{5}$/, 'Expected a rate like "0.08250"');
exports.zUpdateTenantSettingsRequest = zod_1.z
    .object({
    partsTaxRate: zRate,
    laborTaxRate: zRate,
    subletTaxRate: zRate,
    // Whether each category's rate prints as its own small line under
    // "Tax" on the estimate/invoice PDF — a live rendering preference,
    // never snapshotted (unlike the rate values themselves).
    partsTaxRateVisible: zod_1.z.boolean(),
    laborTaxRateVisible: zod_1.z.boolean(),
    subletTaxRateVisible: zod_1.z.boolean(),
    // shopSuppliesBasis is deliberately NOT editable — PricingService only
    // implements the 'parts_subtotal' basis (confirmed by the shop owner
    // as the actual rule; see docs/IMPLEMENTATION_PLAN.md), so exposing a
    // knob with no corresponding code path would just be confusing.
    shopSuppliesRate: zRate,
    shopSuppliesCap: money_js_1.zMoney.nullable(),
    shopSuppliesTaxable: zod_1.z.boolean(),
    // Master on/off switch for Shop Supplies as a whole — see the doc
    // comment on this column in apps/api/src/db/schema/tenancy.ts.
    shopSuppliesEnabled: zod_1.z.boolean(),
    defaultLaborRate: money_js_1.zMoney,
    invoiceTermsText: zod_1.z.string().trim(),
    customerNumberPrefix: zod_1.z.string().trim().min(1).nullable(),
    invoiceNumberPrefix: zod_1.z.string().trim().min(1).nullable(),
    estimateNumberPrefix: zod_1.z.string().trim().min(1),
    // How long a freshly-issued session cookie stays valid — read at
    // cookie-issue time (login/select-tenant/password-change), never
    // retroactively applied to a cookie already handed out. See
    // AuthService's own session-TTL helper.
    sessionTtlHours: zod_1.z.number().int().min(1).max(24 * 30),
    // How long a "remember this device" trusted-device cookie stays valid
    // after 2FA verification — read at the moment that cookie is issued,
    // same non-retroactive rule as sessionTtlHours. Matches the DB's own
    // CHECK constraint (migration 0018).
    trustedDeviceDays: zod_1.z.number().int().min(7).max(30),
    // Master on/off switch for the Vehicle Image URL field/thumbnails —
    // see the doc comment on this column in apps/api/src/db/schema/tenancy.ts.
    vehicleImageEnabled: zod_1.z.boolean(),
    // Replaces the literal word "Tax" everywhere it prints (work order
    // Totals card, invoice detail page, invoice/estimate PDFs) — a Canada-
    // motivated field (HST only applies in 5 of 10 provinces; the rest use
    // GST+PST, GST+QST, or GST alone, so a hardcoded "HST instead of Tax"
    // toggle would mislabel most Canadian shops), but plain free text so
    // any shop can set it, not just Canadian ones. Settings UI only
    // surfaces this field when the shop's country is Canada; the column
    // itself has no such restriction.
    taxLabel: zod_1.z.string().trim().min(1).max(40),
    // The CRA's GST/HST business number — covers both taxes under one
    // registration regardless of which applies in the shop's province, so
    // one field is correct for every Canadian shop. Null hides it
    // everywhere it would otherwise print (see InvoicesService.issue()'s
    // shopTaxRegistrationNumberSnapshot and estimate-view-model.ts).
    taxRegistrationNumber: zod_1.z.string().trim().min(1).nullable(),
    // A tenant-typed label for one generic free-text field on the work
    // order (e.g. "License Plate" for a detailing shop type) — null hides
    // the field everywhere it would otherwise appear (work order editor,
    // estimate/invoice rendering), same convention as
    // taxRegistrationNumber above. See work_orders.flexField1's own doc
    // comment and docs/SHOP_TYPES_PLAN.md.
    flexField1Label: zod_1.z.string().trim().min(1).max(40).nullable(),
})
    .partial();
//# sourceMappingURL=tenant-settings.js.map